<template>
  <div :class="$style.emailLoginContainer">
    <div :class="$style.header">
      <h2 :class="$style.title">이메일 로그인</h2>
    </div>

    <form @submit.prevent="handleEmailLogin" :class="$style.loginForm" novalidate>
      <!-- 이메일 입력 -->
      <div :class="$style.inputGroup">
        <label :class="$style.label" for="loginEmail">이메일</label>
        <input id="loginEmail" v-model="formData.email" type="email"
          :class="[$style.input, { [$style.error]: errors.email }]" placeholder="이메일을 입력해주세요" autocomplete="email" />
        <span v-if="errors.email" :class="$style.errorText">{{
          errors.email
        }}</span>
      </div>

      <!-- 비밀번호 입력 -->
      <div :class="$style.inputGroup">
        <label :class="$style.label" for="loginPassword">비밀번호</label>
        <input id="loginPassword" v-model="formData.password" type="password"
          :class="[$style.input, { [$style.error]: errors.password }]" placeholder="비밀번호를 입력해주세요"
          autocomplete="current-password" />
        <span v-if="errors.password" :class="$style.errorText">{{
          errors.password
        }}</span>
      </div>

      <!-- 로그인 버튼 -->
      <button type="submit" :class="$style.submitButton" :disabled="isLoading">
        {{ isLoading ? "로그인 중..." : "로그인" }}
      </button>
    </form>

    <!-- 소셜 로그인 구분선 -->
    <div :class="$style.divider">
      <span :class="$style.dividerText">또는</span>
    </div>

    <!-- 소셜 로그인 버튼들 -->
    <div :class="$style.socialButtons">
      <button :class="[$style.socialButton, $style.kakaoButton]" @click="kakaoLogin">
        <img :class="$style.socialIcon" src="@/assets/images/kakao_fill.png" alt="kakao icon" />
        <span :class="$style.socialText">카카오로 계속하기</span>
      </button>
    </div>

    <!-- 비밀번호 찾기 링크 -->
    <div :class="$style.forgotPasswordLink">
      <button type="button" @click="showPasswordResetModal" :class="$style.linkButton">
        비밀번호를 잊으셨나요?
      </button>
    </div>

    <!-- 회원가입 링크 -->
    <div :class="$style.signupLink">
      <span :class="$style.signupText">계정이 없으신가요?</span>
      <button type="button" @click="$emit('switchToSignup')" :class="$style.linkButton">
        회원가입하기
      </button>
    </div>

    <!-- 비밀번호 재설정 모달 -->
    <div v-if="showPasswordReset" :class="$style.modalOverlay" @click="hidePasswordResetModal">
      <div :class="$style.modalContent" @click.stop>
        <div :class="$style.modalHeader">
          <h3 :class="$style.modalTitle">비밀번호 재설정</h3>
          <button @click="hidePasswordResetModal" :class="$style.closeButton">
            ×
          </button>
        </div>
        <div :class="$style.modalBody">
          <p :class="$style.modalDescription">
            등록하신 이메일 주소를 입력하시면<br />
            비밀번호 재설정 링크를 보내드립니다.
          </p>
          <form @submit.prevent="sendPasswordReset" :class="$style.resetForm">
            <div :class="$style.inputGroup">
              <label :class="$style.label" for="resetEmail">이메일</label>
              <input id="resetEmail" v-model="resetEmail" type="email"
                :class="[$style.input, { [$style.error]: resetEmailError }]" placeholder="이메일을 입력해주세요"
                autocomplete="email" />
              <span v-if="resetEmailError" :class="$style.errorText">{{
                resetEmailError
              }}</span>
            </div>
            <div :class="$style.modalActions">
              <button type="button" @click="hidePasswordResetModal" :class="[$style.modalButton, $style.cancelButton]">
                취소
              </button>
              <button type="submit" :class="[$style.modalButton, $style.sendButton]" :disabled="isResetLoading">
                {{ isResetLoading ? "전송 중..." : " 보내기" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, storage } from "/firebaseConfig";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { handleLogin } from "@/utils/write/user";
import { trackLoginActivity, getDeviceInfo } from "@/utils/activityTracker";

export default {
  name: "EmailLoginForm",
  emits: ["back", "switchToSignup", "success"],
  data() {
    return {
      isLoading: false,
      formData: {
        email: "",
        password: "",
      },
      errors: {},
      showPasswordReset: false,
      resetEmail: "",
      resetEmailError: "",
      isResetLoading: false,
    };
  },
  methods: {
    validateForm() {
      this.errors = {};

      // 이메일 검증
      if (!this.formData.email) {
        this.errors.email = "이메일을 입력해주세요.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)) {
        this.errors.email = "올바른 이메일 형식을 입력해주세요.";
      }

      // 비밀번호 검증
      if (!this.formData.password) {
        this.errors.password = "비밀번호를 입력해주세요.";
      }

      return Object.keys(this.errors).length === 0;
    },

    async handleEmailLogin() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;

      try {
        // 1. Firebase Auth로 로그인하여 ID Token 획득
        const userCredential = await signInWithEmailAndPassword(
          auth,
          this.formData.email,
          this.formData.password
        );
        const idToken = await userCredential.user.getIdToken();

        // 2. 로그인 처리 (통합 API - 사용자 정보 조회 + lastLogin 업데이트 + loginHistory 기록 + Custom Token 발급)
        const deviceInfo = getDeviceInfo();
        const userData = await handleLogin(this.formData.email, "email", deviceInfo, idToken);

        // 로그인 활동 기록
        await trackLoginActivity("email_signup");

        // Firebase Storage에서 프로필 이미지 로드
        let profileImage = null;
        if (userData.img) {
          try {
            const imageRef = storageRef(storage, `profile/${userData.img}`);
            profileImage = await getDownloadURL(imageRef);
          } catch (error) {
            console.warn("프로필 이미지 로드 실패:", error);
            profileImage = null;
          }
        }

        // 성공 이벤트 발생 - Firebase UID와 모든 필요한 정보 포함
        const currentUser = auth.currentUser;
        this.$emit("success", {
          email: userData.email || this.formData.email,
          uid: currentUser?.uid || userData.uid || null,
          nickname: userData.nickname || this.formData.email.split("@")[0],
          auth: userData.auth || null,
          profileImage: profileImage,
          artistLikes: userData.artistLikes || [],
          liveLikes: userData.liveLikes || [],
          firebaseAuthEnabled: !!currentUser,
          levelPoint: userData.levelPoint || 0,
          isBlocked: userData.isBlocked || false,
        });
      } catch (error) {
        console.error("이메일 로그인 오류:", error);

        // 사용자 정보를 찾을 수 없는 경우 (API 에러)
        if (error.response && error.response.status === 404) {
          this.errors.email = "사용자 정보를 찾을 수 없습니다. 회원가입을 해주세요.";
          return;
        }

        // Firebase Auth 에러 처리
        if (error.code === "auth/user-not-found") {
          this.errors.email = "등록되지 않은 이메일입니다.";
        } else if (error.code === "auth/wrong-password") {
          this.errors.password = "비밀번호가 올바르지 않습니다.";
        } else if (error.code === "auth/invalid-credential") {
          this.errors.password = "이메일 또는 비밀번호가 올바르지 않습니다.";
        } else if (error.code === "auth/invalid-email") {
          this.errors.email = "유효하지 않은 이메일 주소입니다.";
        } else if (error.code === "auth/user-disabled") {
          this.errors.email = "비활성화된 계정입니다.";
        } else if (error.code === "auth/too-many-requests") {
          this.$alert(
            "로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요."
          );
        } else if (error.code === "auth/network-request-failed") {
          this.$alert("네트워크 연결을 확인해주세요.");
        } else {
          this.$alert(`로그인에 실패했습니다`);
        }
      } finally {
        this.isLoading = false;
      }
    },

    kakaoLogin() {
      // 카카오 로그인
      window.Kakao.Auth.authorize({
        redirectUri: process.env.VUE_APP_BASE_URL + "/kakaoLogin",
        throughTalk: false,
        scope: 'profile_nickname,account_email',
      });
    },

    // 비밀번호 재설정 모달 표시
    showPasswordResetModal() {
      this.showPasswordReset = true;
      this.resetEmail = this.formData.email; // 현재 입력된 이메일을 기본값으로 설정
      this.resetEmailError = "";
    },

    // 비밀번호 재설정 모달 숨기기
    hidePasswordResetModal() {
      this.showPasswordReset = false;
      this.resetEmail = "";
      this.resetEmailError = "";
      this.isResetLoading = false;
    },

    // 비밀번호 재설정 이메일 검증
    validateResetEmail() {
      this.resetEmailError = "";

      if (!this.resetEmail) {
        this.resetEmailError = "이메일을 입력해주세요.";
        return false;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.resetEmail)) {
        this.resetEmailError = "올바른 이메일 형식을 입력해주세요.";
        return false;
      }

      return true;
    },

    // 비밀번호 재설정 이메일 전송
    async sendPasswordReset() {
      if (!this.validateResetEmail()) {
        return;
      }

      this.isResetLoading = true;

      try {
        await sendPasswordResetEmail(auth, this.resetEmail, {
          url: window.location.origin, // 재설정 후 돌아올 URL
          handleCodeInApp: false,
        });

        this.$alert(
          `${this.resetEmail}로 비밀번호 재설정 이메일을 보냈습니다.\n\n이메일을 확인하시고 안내에 따라 비밀번호를 재설정해주세요.\n(스팸함도 확인해주시기 바랍니다)`,
          "이메일 전송 완료"
        );

        this.hidePasswordResetModal();
      } catch (error) {
        console.error("비밀번호 재설정 이메일 전송 실패:", error);

        // Firebase Auth 에러 처리
        if (error.code === "auth/user-not-found") {
          this.resetEmailError = "등록되지 않은 이메일 주소입니다.";
        } else if (error.code === "auth/invalid-email") {
          this.resetEmailError = "유효하지 않은 이메일 주소입니다.";
        } else if (error.code === "auth/too-many-requests") {
          this.$alert(
            "너무 많은 요청이 발생했습니다. 잠시 후 다시 시도해주세요."
          );
        } else if (error.code === "auth/network-request-failed") {
          this.$alert("네트워크 연결을 확인해주세요.");
        } else {
          this.$alert("이메일 전송에 실패했습니다. 다시 시도해주세요.");
        }
      } finally {
        this.isResetLoading = false;
      }
    },
  },
};
</script>

<style module>
.emailLoginContainer {
  max-width: 400px;
  margin: 0 auto;
  padding: 30px 20px;
}

.header {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  position: relative;
}

.backButton {
  background: none;
  border: none;
  color: #666;
  font-size: 1rem;
  cursor: pointer;
  padding: 8px;
  margin-right: 10px;
  transition: color 0.2s ease;
}

.backButton:hover {
  color: #333;
}

.title {
  font-size: 1.8rem;
  font-weight: bold;
  color: #333;
  margin: 0;
  flex: 1;
  text-align: center;
}

.loginForm {
  margin-bottom: 30px;
}

.inputGroup {
  margin-bottom: 20px;
}

.label {
  display: block;
  text-align: left;
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
}

.input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.input:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.1);
}

.input.error {
  border-color: #ff6b6b;
}

.errorText {
  display: block;
  margin-top: 5px;
  font-size: 0.8rem;
  color: #ff6b6b;
}

.submitButton {
  width: 100%;
  padding: 12px 20px;
  background: #66bdff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.submitButton:hover:not(:disabled) {
  background: #4a9eff;
  transform: translateY(-1px);
}

.submitButton:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

.divider {
  position: relative;
  text-align: center;
  margin: 30px 0;
}

.divider::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e0e0e0;
}

.dividerText {
  background: white;
  padding: 0 5px;
  color: #999;
  font-size: 0.9rem;
  position: relative;
  z-index: 1;
}

.socialButtons {
  margin-bottom: 25px;
}

.socialButton {
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  background: #fee502;
  color: #333;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.socialButton:hover {
  background: #fdd835;
}

.socialIcon {
  width: 20px;
  height: 20px;
}

.socialText {
  font-size: 1rem;
}

.signupLink {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.signupText {
  font-size: 0.9rem;
  color: #666;
  margin-right: 8px;
}

.linkButton {
  background: none;
  border: none;
  color: #66bdff;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
}

.linkButton:hover {
  color: #4a9eff;
}

.forgotPasswordLink {
  text-align: center;
  margin-bottom: 15px;
}

/* 비밀번호 재설정 모달 스타일 */
.modalOverlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modalContent {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 450px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modalHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 25px 15px;
  border-bottom: 1px solid #e0e0e0;
}

.modalTitle {
  font-size: 1.4rem;
  font-weight: bold;
  color: #333;
  margin: 0;
}

.closeButton {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.closeButton:hover {
  background: #f5f5f5;
  color: #666;
}

.modalBody {
  padding: 25px;
}

.modalDescription {
  text-align: center;
  color: #666;
  margin-bottom: 25px;
  line-height: 1.5;
  font-size: 0.95rem;
}

.resetForm {
  margin: 0;
}

.modalActions {
  display: flex;
  gap: 10px;
  margin-top: 25px;
}

.modalButton {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancelButton {
  background: #f5f5f5;
  color: #666;
}

.cancelButton:hover {
  background: #e9ecef;
}

.sendButton {
  background: #66bdff;
  color: white;
}

.sendButton:hover:not(:disabled) {
  background: #4a9eff;
  transform: translateY(-1px);
}

.sendButton:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

/* 모바일 반응형 */
@media (max-width: 480px) {
  .emailLoginContainer {
    padding: 20px 15px;
  }

  .title {
    font-size: 1.5rem;
  }

  .input {
    padding: 10px 14px;
  }

  .submitButton {
    padding: 10px 16px;
    font-size: 0.9rem;
  }

  .socialButton {
    padding: 10px 14px;
    font-size: 0.9rem;
  }

  .socialIcon {
    width: 18px;
    height: 18px;
  }

  /* 모달 모바일 반응형 */
  .modalOverlay {
    padding: 10px;
  }

  .modalContent {
    max-width: 100%;
  }

  .modalHeader {
    padding: 15px 20px 10px;
  }

  .modalTitle {
    font-size: 1.2rem;
  }

  .modalBody {
    padding: 20px;
  }

  .modalDescription {
    font-size: 0.9rem;
    margin-bottom: 20px;
  }

  .modalActions {
    flex-direction: column;
    gap: 10px;
  }

  .modalButton {
    padding: 10px 16px;
    font-size: 0.9rem;
  }
}
</style>
